/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { CHANNEL_NUMBER_MAX_LENGTH } from '../../../config';
import { VodInteractor } from '../../../core/interactors';
import { Log } from '../../../core/logging/Log';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import type { TGetState, TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { TChannelsV2 } from '../../../core/types';
import { TChannels } from '../../../core/types/TChannels';
import { isJumpChannelCcbSwitchEnabled } from '../../../core/utils/ccb/CcbSwitchHelper';
import { showTemporaryGuideIssueOverlay } from '../../overlay/temporaryguideissueoverlay/temporaryguideissueoverlay.reducers';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { setGridGuideOffsetAction, setIsChannelsServiceFailAction } from '../guide.actions';
import { loadGridGuideChannels } from '../guide.reducers';
import { setVerticalPosition } from '../guidehighlight/guidehighlight.reducers';
import {
    setEnteredChannelNumberAction,
    startEnterChannelNumberAction,
    stopEnterChannelNumberAction,
} from './enterchannelnumber.actions';
import { TEnterChannelNumberState } from './enterchannelnumber.props';
import {
    GUIDE_SET_ENTERED_CHANNEL_NUMBER,
    START_ENTER_CHANNEL_NUMBER,
    STOP_ENTER_CHANNEL_NUMBER,
} from './enterchannelnumber.types';

const TAG = 'enterChannelNumber';
const initialState: TEnterChannelNumberState = {
    isEnterChannelNumberActive: false,
    enteredChannelNumber: '',
};

export const enterChannelNumberReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<TEnterChannelNumberState>>{},
) => {
    switch (type) {
        case START_ENTER_CHANNEL_NUMBER:
        case STOP_ENTER_CHANNEL_NUMBER:
            return {
                ...state,
                isEnterChannelNumberActive: payload.isEnterChannelNumberActive,
                enteredChannelNumber: payload.enteredChannelNumber,
            };
        case GUIDE_SET_ENTERED_CHANNEL_NUMBER:
            return {
                ...state,
                enteredChannelNumber: payload.enteredChannelNumber,
            };
        default:
            return state;
    }
};

export const startEnterChannelNumber = () => (dispatch: Function) => {
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.ENTER_CHANNEL_NUMBER));
    dispatch(startEnterChannelNumberAction());
};

export const stopEnterChannelNumber = () => (dispatch: Function) => {
    dispatch(stopEnterChannelNumberAction());
    dispatch(setVerticalPosition(0));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
};

export const setEnteredChannelNumber = () => (dispatch: Function, getState: TGetState) => {
    const { key } = getState().inputFactory;
    const { enteredChannelNumber } = getState().enterChannelNumber;

    if (enteredChannelNumber.length < CHANNEL_NUMBER_MAX_LENGTH) {
        dispatch(setEnteredChannelNumberAction(`${enteredChannelNumber}${key}`));
    }
};

export const fetchAndUpdateChannelsFromChannelNumber =
    (channelNumber: string) => (dispatch: Function, getState: TGetState) => {
        if (channelNumber.length === 0) return;

        const { LIMIT } = getState().gridGuide;
        const { channelsType } = getState().channelOptionsOverlay;

        const services: VodInteractor = new VodInteractor();
        const fetchNextChannelsFromChannelNumber = isJumpChannelCcbSwitchEnabled()
            ? services.fetchNextChannelsFromChannelNumberV2.bind(services)
            : services.fetchNextChannelsFromChannelNumber.bind(services);

        fetchNextChannelsFromChannelNumber(channelNumber, channelsType, LIMIT)
            .then((response: TChannels | TChannelsV2) => {
                dispatch(setGridGuideOffsetAction(response.currentOffset));
                dispatch(loadGridGuideChannels());
            })
            .catch((error) => {
                dispatch(setIsChannelsServiceFailAction(true));
                dispatch(showTemporaryGuideIssueOverlay());
                Diagnostics.error(
                    TAG,
                    'Failed to fetch next channels from channel number.fetchAndUpdateChannelsFromChannelNumber() -> services.fetchNextChannelsFromChannelNumber',
                    error,
                );
                Log.e(TAG, error);
            });
    };

export const onKeyOkEnterChannelNumber = () => (dispatch: Function, getState: TGetState) => {
    const { enteredChannelNumber } = getState().enterChannelNumber;
    dispatch(fetchAndUpdateChannelsFromChannelNumber(enteredChannelNumber));
    dispatch(stopEnterChannelNumber());
};

export default enterChannelNumberReducer;
